import React from 'react';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck, FileText, Globe, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';

interface PrivacyPolicyPageProps {
  onNavigate: (page: string) => void;
}

export function PrivacyPolicyPage({ onNavigate }: PrivacyPolicyPageProps) {
  const sections = [
    {
      id: 'informacoes-coletadas',
      title: 'Informações que Coletamos',
      icon: Database,
      content: [
        {
          subtitle: 'Informações Pessoais',
          items: [
            'Nome completo, CPF/CNPJ e data de nascimento',
            'Endereço de e-mail e número de telefone',
            'Endereço residencial ou comercial',
            'Informações de pagamento (processadas por parceiros seguros)'
          ]
        },
        {
          subtitle: 'Informações de Uso',
          items: [
            'Dados de navegação e preferências de eventos',
            'Histórico de compras e participação em eventos',
            'Informações do dispositivo e localização (quando autorizado)',
            'Cookies e tecnologias similares'
          ]
        }
      ]
    },
    {
      id: 'como-usamos',
      title: 'Como Usamos suas Informações',
      icon: Eye,
      content: [
        {
          subtitle: 'Prestação de Serviços',
          items: [
            'Processar compras de ingressos e reservas',
            'Enviar confirmações e informações sobre eventos',
            'Fornecer suporte ao cliente',
            'Personalizar sua experiência na plataforma'
          ]
        },
        {
          subtitle: 'Comunicação e Marketing',
          items: [
            'Enviar newsletters e promoções (com seu consentimento)',
            'Notificar sobre eventos que possam interessar',
            'Comunicar atualizações importantes do serviço',
            'Realizar pesquisas de satisfação'
          ]
        }
      ]
    },
    {
      id: 'compartilhamento',
      title: 'Compartilhamento de Informações',
      icon: UserCheck,
      content: [
        {
          subtitle: 'Organizadores de Eventos',
          items: [
            'Compartilhamos informações necessárias para acesso aos eventos',
            'Nome e dados de contato para lista de participantes',
            'Informações específicas solicitadas pelo organizador'
          ]
        },
        {
          subtitle: 'Parceiros de Serviço',
          items: [
            'Processadores de pagamento para transações seguras',
            'Provedores de e-mail para comunicações',
            'Serviços de análise para melhorar nossa plataforma',
            'Todos os parceiros seguem rigorosos padrões de segurança'
          ]
        }
      ]
    },
    {
      id: 'seguranca',
      title: 'Segurança dos Dados',
      icon: Lock,
      content: [
        {
          subtitle: 'Medidas de Proteção',
          items: [
            'Criptografia SSL/TLS para todas as transmissões',
            'Armazenamento seguro em servidores certificados',
            'Acesso restrito e autenticação em duas etapas',
            'Monitoramento contínuo de segurança'
          ]
        },
        {
          subtitle: 'Conformidade',
          items: [
            'Compliance com a LGPD (Lei Geral de Proteção de Dados)',
            'Auditorias regulares de segurança',
            'Políticas internas rigorosas de proteção de dados',
            'Treinamento contínuo da equipe'
          ]
        }
      ]
    },
    {
      id: 'seus-direitos',
      title: 'Seus Direitos',
      icon: Shield,
      content: [
        {
          subtitle: 'Direitos Garantidos pela LGPD',
          items: [
            'Confirmação da existência de tratamento de dados',
            'Acesso aos seus dados pessoais',
            'Correção de dados incompletos, inexatos ou desatualizados',
            'Anonimização, bloqueio ou eliminação de dados desnecessários',
            'Portabilidade dos dados para outro fornecedor',
            'Eliminação dos dados tratados com seu consentimento',
            'Revogação do consentimento'
          ]
        },
        {
          subtitle: 'Como Exercer seus Direitos',
          items: [
            'Entre em contato através do e-mail: privacidade@uniqueingressos.com.br',
            'Utilize o formulário de solicitação em nossa plataforma',
            'Atendimento através do nosso suporte ao cliente',
            'Responderemos sua solicitação em até 15 dias úteis'
          ]
        }
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies e Tecnologias Similares',
      icon: Globe,
      content: [
        {
          subtitle: 'Tipos de Cookies',
          items: [
            'Cookies essenciais para funcionamento da plataforma',
            'Cookies de performance para melhorar a experiência',
            'Cookies de personalização para conteúdo relevante',
            'Cookies de marketing (apenas com seu consentimento)'
          ]
        },
        {
          subtitle: 'Gerenciamento de Cookies',
          items: [
            'Você pode gerenciar cookies nas configurações do navegador',
            'Opção de aceitar ou recusar cookies não essenciais',
            'Configurações disponíveis em nossa central de privacidade',
            'Alguns recursos podem não funcionar sem cookies essenciais'
          ]
        }
      ]
    }
  ];

  const handleContactPrivacy = () => {
    window.open('mailto:privacidade@uniqueingressos.com.br', '_blank');
  };

  return (
    <div className="min-h-screen pt-18 bg-gradient-to-br from-background via-muted/20 to-orange-primary/5">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => onNavigate('home')}
            className="mb-6 text-muted-foreground hover:text-foreground transition-all duration-200 hover:translate-x-1 group"
          >
            <ArrowLeft size={20} className="mr-2 transition-transform duration-200 group-hover:-translate-x-1" />
            Voltar ao início
          </Button>

          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-orange-primary/10 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-orange-primary" />
              </div>
            </div>
            <h1 className="text-3xl lg:text-4xl font-heading mb-4">
              Política de Privacidade
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Transparência e proteção dos seus dados pessoais são fundamentais para nós. 
              Saiba como coletamos, usamos e protegemos suas informações.
            </p>
          </div>

          <Card className="bg-card/50 backdrop-blur-sm border-border/50 mb-8">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <Calendar className="w-6 h-6 text-orange-primary mt-1" />
                <div>
                  <h3 className="font-semibold mb-2">Última Atualização</h3>
                  <p className="text-muted-foreground text-sm">
                    Esta política foi atualizada em 8 de junho de 2025. 
                    Recomendamos que você revise periodicamente este documento para se manter informado sobre nossas práticas de privacidade.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <Card key={section.id} className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-orange-primary/10 rounded-lg flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-orange-primary" />
                  </div>
                  <span>{section.title}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {section.content.map((item, itemIndex) => (
                  <div key={itemIndex}>
                    <h4 className="font-semibold mb-3 text-foreground">
                      {item.subtitle}
                    </h4>
                    <ul className="space-y-2">
                      {item.items.map((listItem, listIndex) => (
                        <li key={listIndex} className="flex items-start space-x-3">
                          <div className="w-1.5 h-1.5 bg-orange-primary rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-muted-foreground">{listItem}</span>
                        </li>
                      ))}
                    </ul>
                    {itemIndex < section.content.length - 1 && (
                      <Separator className="mt-4" />
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Section */}
        <Card className="bg-gradient-to-r from-orange-primary/5 to-orange-primary/10 border-orange-primary/20 mt-8">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-orange-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-4">
              Dúvidas sobre Privacidade?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Se você tiver dúvidas sobre esta política de privacidade ou sobre como tratamos seus dados pessoais, 
              entre em contato conosco. Estamos aqui para esclarecer todas as suas questões.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleContactPrivacy}
                className="bg-orange-primary hover:bg-orange-primary/90 text-white"
              >
                <Shield className="w-4 h-4 mr-2" />
                Contatar sobre Privacidade
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open('https://api.whatsapp.com/send?l=pt&phone=5582996064988', '_blank')}
              >
                Suporte Geral
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
        </div>
      </div>
    </div>
  );
}